/**
 * Ticket 138.6 — typed API client for the conversation routing and escalation
 * ADMIN surface (backend: `internal/routingadmin`, decisions D18/D19).
 *
 * Deliberately vocabulary-free. Every enumerable value a rule, a schedule or a
 * destination can carry is served by `GET /routing/catalog` and travels through
 * `RoutingCatalog`. Nothing in this file declares a predicate kind, an
 * operator, a routing mode or a weekday as a TypeScript literal, because a
 * literal here is a second source of truth that drifts from the Go enum the
 * engine evaluates — and drifts silently, since a duplicated list still
 * compiles. This mirrors 137.7's connector catalogue.
 *
 * There is no exception: `mode`, `kind`, `operator` and the weekday codes are
 * all typed as `string`, and the values a form may offer come from
 * `RoutingCatalog` at render time. The page discriminates on the mode it was
 * SERVED, never on a union declared here.
 */

import { apiClient } from '@/lib/apiClient';

// ------------------------------------------------------------------ //
// Catalogue (GET /routing/catalog)
// ------------------------------------------------------------------ //

/**
 * One predicate kind and everything the rule builder needs to render it: which
 * operators it admits, whether it needs a key, and — when the value is an
 * enum — the exact set of values the backend will accept.
 *
 * `valueType` picks the input widget. `valueVocabulary` empty means free text
 * or a uuid, for which the backend imposes no set.
 */
export interface PredicateKindSpec {
  kind: string;
  operators: string[];
  valueType: string;
  requiresKey?: boolean;
  requiresClassifier?: boolean;
  keyVocabulary?: string[];
  valueVocabulary?: string[];
}

export interface RoutingCatalog {
  modes: string[];
  destinationKinds: string[];
  operators: string[];
  weekdays: string[];
  predicateKinds: PredicateKindSpec[];
  escalationTriggers: string[];
  slaKinds: string[];
  routingStatuses: string[];
}

export async function getRoutingCatalog(): Promise<RoutingCatalog> {
  const { data } = await apiClient.get<RoutingCatalog>('/routing/catalog');
  return data;
}

// ------------------------------------------------------------------ //
// Shared shapes
// ------------------------------------------------------------------ //

/**
 * A destination is a QUEUE or a USER, never both — the backend encodes this as
 * two nullable columns under a `num_nonnulls = 1` CHECK, and the wire shape
 * collapses that into a discriminated pair so the UI cannot express the
 * impossible state.
 */
export interface Destination {
  kind: string;
  id: string;
}

/**
 * One business-hours window. `days` empty means EVERY day.
 *
 * An empty window LIST means always open. That direction is easy to get
 * backwards and it is load-bearing: a schedule that failed to parse degrades to
 * an always-open line rather than one that never answers.
 */
export interface BusinessHoursWindow {
  days: string[];
  start: string;
  end: string;
}

// ------------------------------------------------------------------ //
// Queues (GET/POST/PUT/DELETE /routing/queues)
// ------------------------------------------------------------------ //

export interface RoutingQueue {
  id: string;
  name: string;
  isActive: boolean;
  isDefault: boolean;
  businessHours: BusinessHoursWindow[];
  timezone: string;
  memberCount: number;
  /** Zero here is what makes the "ladder ends nowhere" warning legible. */
  activeMemberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface QueueInput {
  name: string;
  isActive?: boolean;
  isDefault?: boolean;
  businessHours: BusinessHoursWindow[];
  timezone: string;
}

export async function listRoutingQueues(): Promise<RoutingQueue[]> {
  const { data } = await apiClient.get<RoutingQueue[]>('/routing/queues');
  return data ?? [];
}

export async function createRoutingQueue(input: QueueInput): Promise<RoutingQueue> {
  const { data } = await apiClient.post<RoutingQueue>('/routing/queues', input);
  return data;
}

export async function updateRoutingQueue(id: string, input: QueueInput): Promise<RoutingQueue> {
  const { data } = await apiClient.put<RoutingQueue>(`/routing/queues/${id}`, input);
  return data;
}

export async function deleteRoutingQueue(id: string): Promise<void> {
  await apiClient.delete(`/routing/queues/${id}`);
}

// ------------------------------------------------------------------ //
// Queue members
// ------------------------------------------------------------------ //

export interface QueueMember {
  id: string;
  userId: string;
  email: string;
  role: string;
  isActive: boolean;
  /** null means NO ceiling, which is what a member created without one gets. */
  maxOpenConversations: number | null;
  assignedCount: number;
  lastAssignedAt: string | null;
}

export interface MemberInput {
  userId?: string;
  isActive?: boolean;
  maxOpenConversations?: number | null;
}

export async function listQueueMembers(queueId: string): Promise<QueueMember[]> {
  const { data } = await apiClient.get<QueueMember[]>(`/routing/queues/${queueId}/members`);
  return data ?? [];
}

export async function addQueueMember(queueId: string, input: MemberInput): Promise<QueueMember> {
  const { data } = await apiClient.post<QueueMember>(`/routing/queues/${queueId}/members`, input);
  return data;
}

export async function updateQueueMember(memberId: string, input: MemberInput): Promise<QueueMember> {
  const { data } = await apiClient.put<QueueMember>(`/routing/members/${memberId}`, input);
  return data;
}

export async function removeQueueMember(memberId: string): Promise<void> {
  await apiClient.delete(`/routing/members/${memberId}`);
}

// ------------------------------------------------------------------ //
// Escalation ladder
// ------------------------------------------------------------------ //

export interface EscalationTier {
  id: string;
  queueId: string;
  position: number;
  name: string;
  destination: Destination | null;
  firstResponseSeconds: number;
  nextResponseSeconds: number;
  isActive: boolean;
}

/**
 * A queue's rungs plus the finding SQL cannot express.
 *
 * `warning` carries the backend's "the ladder ends nowhere" message when the
 * terminal rung escalates into a queue with no active member. It is a warning,
 * not a rejection: an admin builds a ladder rung by rung and staffs the queue
 * afterwards. What must not happen is the finding living only in a server log,
 * which is where it lived until this ticket.
 */
export interface EscalationLadder {
  queueId: string;
  tiers: EscalationTier[];
  warning?: string;
}

export interface TierInput {
  position: number;
  name: string;
  destination: Destination;
  firstResponseSeconds: number;
  nextResponseSeconds: number;
  isActive?: boolean;
}

export async function getEscalationLadder(queueId: string): Promise<EscalationLadder> {
  const { data } = await apiClient.get<EscalationLadder>(`/routing/queues/${queueId}/ladder`);
  return data;
}

export async function createEscalationTier(
  queueId: string,
  input: TierInput,
): Promise<EscalationTier> {
  const { data } = await apiClient.post<EscalationTier>(
    `/routing/queues/${queueId}/tiers`,
    input,
  );
  return data;
}

export async function updateEscalationTier(
  tierId: string,
  input: TierInput,
): Promise<EscalationTier> {
  const { data } = await apiClient.put<EscalationTier>(`/routing/tiers/${tierId}`, input);
  return data;
}

export async function deleteEscalationTier(tierId: string): Promise<void> {
  await apiClient.delete(`/routing/tiers/${tierId}`);
}

// ------------------------------------------------------------------ //
// Line routing configuration and rules
// ------------------------------------------------------------------ //

export interface LineRoutingConfig {
  /** Empty-ish uuid when the line has no row yet: an unconfigured line is not an error. */
  id: string;
  lineId: string;
  mode: string;
  staticDestination: Destination | null;
  fallbackQueueId: string | null;
  businessHours: BusinessHoursWindow[];
  timezone: string;
  isActive: boolean;
}

export interface LineConfigInput {
  mode: string;
  staticDestination?: Destination | null;
  fallbackQueueId?: string | null;
  businessHours: BusinessHoursWindow[];
  timezone: string;
  isActive?: boolean;
}

export interface RulePredicate {
  kind: string;
  key: string;
  operator: string;
  value: string;
}

export interface RoutingRule {
  id: string;
  name: string;
  priority: number;
  destination: Destination;
  isActive: boolean;
  predicates: RulePredicate[];
}

export interface RuleInput {
  name: string;
  priority?: number;
  destination: Destination;
  isActive?: boolean;
  predicates: RulePredicate[];
}

export async function getLineRoutingConfig(lineId: string): Promise<LineRoutingConfig> {
  const { data } = await apiClient.get<LineRoutingConfig>(`/routing/lines/${lineId}/config`);
  return data;
}

export async function putLineRoutingConfig(
  lineId: string,
  input: LineConfigInput,
): Promise<LineRoutingConfig> {
  const { data } = await apiClient.put<LineRoutingConfig>(
    `/routing/lines/${lineId}/config`,
    input,
  );
  return data;
}

export async function listRoutingRules(lineId: string): Promise<RoutingRule[]> {
  const { data } = await apiClient.get<RoutingRule[]>(`/routing/lines/${lineId}/rules`);
  return data ?? [];
}

export async function createRoutingRule(lineId: string, input: RuleInput): Promise<RoutingRule> {
  const { data } = await apiClient.post<RoutingRule>(`/routing/lines/${lineId}/rules`, input);
  return data;
}

export async function updateRoutingRule(ruleId: string, input: RuleInput): Promise<RoutingRule> {
  const { data } = await apiClient.put<RoutingRule>(`/routing/rules/${ruleId}`, input);
  return data;
}

export async function deleteRoutingRule(ruleId: string): Promise<void> {
  await apiClient.delete(`/routing/rules/${ruleId}`);
}

/**
 * Reorders a line's rules. The list must name EVERY rule of the line exactly
 * once — the backend refuses a partial reorder, because rule order is the whole
 * semantics of a first-match-wins engine and a half-applied reorder leaves the
 * unnamed rules interleaved in an order the admin never saw.
 */
export async function reorderRoutingRules(
  lineId: string,
  ruleIds: string[],
): Promise<RoutingRule[]> {
  const { data } = await apiClient.post<RoutingRule[]>(
    `/routing/lines/${lineId}/rules/reorder`,
    { ruleIds },
  );
  return data ?? [];
}

// ------------------------------------------------------------------ //
// Errors
// ------------------------------------------------------------------ //

/**
 * Pulls the backend's explanation out of an axios error.
 *
 * The routing surface answers a malformed business-hours document or an
 * unknown predicate with a 400 whose `detail` NAMES the fault ("predicate 0:
 * operator \"CONTAINS\" is not allowed for kind CHANNEL"). Swallowing that in
 * favour of a generic message would put the admin back where 138.4 left them:
 * told that something was refused, not what.
 */
export function routingErrorDetail(err: unknown): string | null {
  const response = (err as { response?: { data?: { detail?: string; error?: string } } })?.response;
  return response?.data?.detail ?? response?.data?.error ?? null;
}
